import type { ShipperEarningsView } from '@khaddi/contracts';
import { ErrorNote, Icon, Screen, Spinner, Text, colors, formatVnd, formatVndCompact, radii, useApi, useAsync, useLayout, brandSurface } from '@khaddi/ui';
import { View } from 'react-native';
import { useShipperNav } from '../components/ShipperNav';
import { TabHeader } from '../components/TabHeader';
import { dayMonthOf, weekdayOf } from '../format';

const BAR_AREA = 120;

const WeekBars = ({ days }: { days: ShipperEarningsView['days'] }) => {
  // API lists today first; the chart reads left-to-right, oldest to today
  const chronological = [...days].reverse();
  const max = Math.max(1, ...chronological.map((d) => d.earnings));
  return (
    <View accessibilityLabel="Thu nhập 7 ngày" style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 8, height: BAR_AREA + 44 }}>
      {chronological.map((day, index) => {
        const isToday = index === chronological.length - 1;
        const height = day.earnings > 0 ? Math.max(6, Math.round((day.earnings / max) * BAR_AREA)) : 3;
        return (
          <View key={day.date} accessibilityLabel={`${weekdayOf(day.date)} ${dayMonthOf(day.date)}: ${formatVnd(day.earnings)}, ${day.orders} đơn`} style={{ flex: 1, alignItems: 'center', gap: 4 }}>
            <Text variant="label" color={colors.text} style={{ fontSize: 10 }} numeric>
              {day.earnings > 0 ? formatVndCompact(day.earnings) : ''}
            </Text>
            <View style={{ width: '100%', height, borderRadius: 6, backgroundColor: isToday ? colors.gold : colors.sky }} />
            <Text variant="label" color={isToday ? colors.text : colors.text} style={{ fontSize: 11, fontWeight: isToday ? '800' : '600' }}>
              {isToday ? 'Nay' : weekdayOf(day.date)}
            </Text>
          </View>
        );
      })}
    </View>
  );
};

const EarningsBody = ({ data, wide }: { data: ShipperEarningsView; wide: boolean }) => {
  const summary = (
    <View style={{ ...brandSurface, borderRadius: radii.xl, padding: 18, gap: 16 }}>
      <View style={{ flexDirection: 'row', gap: 8 }}>
        <View style={{ flex: 1 }}>
          <Text variant="caption" color={colors.text}>
            Phí ship 7 ngày
          </Text>
          <Text variant="display" color={colors.text} numeric>
            {formatVnd(data.totalEarnings)}
          </Text>
        </View>
        <View style={{ alignItems: 'flex-end' }}>
          <Text variant="caption" color={colors.text}>
            Đơn đã giao
          </Text>
          <Text variant="display" color={colors.text} numeric>
            {String(data.totalOrders)}
          </Text>
        </View>
      </View>
      <WeekBars days={data.days} />
    </View>
  );
  const cod = (
    <View style={{ backgroundColor: colors.goldSoft, borderRadius: radii.lg, padding: 16, flexDirection: 'row', gap: 12, alignItems: 'center' }}>
      <View style={{ width: 44, height: 44, borderRadius: 22, backgroundColor: colors.gold, alignItems: 'center', justifyContent: 'center' }}>
        <Icon name="wallet" size={22} color={colors.navy} />
      </View>
      <View style={{ flex: 1, gap: 2 }}>
        <Text variant="label" color={colors.goldText} style={{ fontSize: 12 }}>
          COD đang giữ
        </Text>
        <Text variant="title" color={colors.goldText} numeric>
          {formatVnd(data.codHolding)}
        </Text>
        <Text variant="caption" color={colors.goldText}>
          Nộp lại tiền COD cho quán cuối ca để được đối soát và nhận phí ship.
        </Text>
      </View>
    </View>
  );
  const days = (
    <View style={{ backgroundColor: colors.surface, borderRadius: radii.lg, paddingHorizontal: 14, paddingVertical: 4 }}>
      {data.days.map((day) => (
        <View key={day.date} style={{ flexDirection: 'row', alignItems: 'center', minHeight: 52, borderBottomWidth: 1, borderBottomColor: colors.divider, gap: 10 }}>
          <View style={{ width: 52 }}>
            <Text variant="bodyStrong">{weekdayOf(day.date)}</Text>
            <Text variant="caption">{dayMonthOf(day.date)}</Text>
          </View>
          <Text variant="caption" style={{ flex: 1 }}>
            {day.orders} đơn{day.cod > 0 ? ` · COD ${formatVndCompact(day.cod)}` : ''}
          </Text>
          <Text variant="bodyStrong" numeric>
            {formatVnd(day.earnings)}
          </Text>
        </View>
      ))}
    </View>
  );
  if (!wide) {
    return (
      <View style={{ paddingHorizontal: 16, gap: 12 }}>
        {summary}
        {cod}
        {days}
      </View>
    );
  }
  return (
    <View style={{ paddingHorizontal: 16, flexDirection: 'row', alignItems: 'flex-start', gap: 12 }}>
      <View style={{ flex: 1, minWidth: 0, gap: 12 }}>
        {summary}
        {cod}
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>{days}</View>
    </View>
  );
};

export const EarningsScreen = () => {
  const nav = useShipperNav('earnings');
  const { wide } = useLayout();
  const api = useApi();
  const earnings = useAsync(() => api.shipper.earnings(), []);
  const data = earnings.data;

  return (
    <Screen nav={nav}>
      <TabHeader title="Thu nhập" subtitle="7 ngày gần nhất" />
      {earnings.loading && !data ? <Spinner /> : null}
      {earnings.error && !data ? <ErrorNote message={earnings.error} onRetry={() => void earnings.reload()} /> : null}
      {data ? <EarningsBody data={data} wide={wide} /> : null}
    </Screen>
  );
};
